/*!
 * ua.collection.order
 * Reorderable collection workspace for dashboards, widgets, and page editor items.
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.collection.order",
  title: "Collection Order Workspace",
  description: "A reorderable card workspace that can render direct items or load paginated items through dashboard APIs.",
  control: true,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Logical control name.",
      required: true
    },
    title: {
      title: "Title",
      type: "string",
      description: "Optional accessible title for the workspace."
    },
    description: {
      title: "Description",
      type: "string",
      description: "Optional helper text shown above the workspace."
    },
    filters: {
      title: "Filters",
      type: "array",
      description: "Optional filter definitions. Rows support { name, label, description, options:[{value,label,icon}], icon, buttonLabel, preview, multi, iconOnly, quickToggle }."
    },
    items: {
      title: "Items",
      type: "array",
      of: "object",
      description: "Direct item list for the workspace."
    },
    "items-json": {
      title: "Items JSON",
      type: "string",
      description: "Serialized item list for the workspace."
    },
    "page-namespace": {
      title: "Page Namespace",
      type: "string",
      description: "Dashboard page namespace used for secure API loading or saving."
    },
    "api-url": {
      title: "API URL",
      type: "string",
      description: "Explicit dashboard API base URL."
    },
    "api-filters": {
      title: "API Filters",
      type: "object",
      description: "Static filters sent with each API list request."
    },
    searchable: {
      title: "Searchable",
      type: "boolean",
      description: "Show a search input.",
      default: false
    },
    "search-placeholder": {
      title: "Search Placeholder",
      type: "string",
      description: "Placeholder text for the search field.",
      default: "Search..."
    },
    editable: {
      title: "Editable",
      type: "boolean",
      description: "Allow entering reorder mode.",
      default: false
    },
    "show-toolbar": {
      title: "Show Toolbar",
      type: "boolean",
      description: "Render the built-in search/filter/control toolbar.",
      default: true
    },
    "show-remove-button": {
      title: "Show Remove Button",
      type: "boolean",
      description: "Render the built-in remove button during edit mode when no custom action button exists.",
      default: true
    },
    "pin-visibility": {
      title: "Pin Visibility",
      type: "string",
      description: "When pin-style action buttons should show. Use 'always' for catalogs and dialogs, or 'edit' to only show them during arrange mode.",
      default: "always"
    },
    "edit-label": {
      title: "Edit Label",
      type: "string",
      description: "Label for the edit button.",
      default: "Edit"
    },
    "show-locked-toggle": {
      title: "Show Locked Toggle",
      type: "boolean",
      description: "Show a locked-items visibility toggle.",
      default: false
    },
    "locked-toggle-label": {
      title: "Locked Toggle Label",
      type: "string",
      description: "Label shown next to the locked-items toggle.",
      default: "Locked Items"
    },
    "show-locked": {
      title: "Show Locked",
      type: "boolean",
      description: "Whether locked items are visible by default.",
      default: true
    },
    "empty-message": {
      title: "Empty Message",
      type: "string",
      description: "Message shown when no items are available.",
      default: "No items found."
    },
    "page-size-sequence": {
      title: "Page Size Sequence",
      type: "string",
      description: "Initial reveal sequence for infinite scroll, as comma separated counts.",
      default: "25,25,30"
    },
    "api-page-size": {
      title: "API Page Size",
      type: "number",
      description: "Fixed backend page size used when loading more items from the API.",
      default: 30
    },
    "save-mode": {
      title: "Save Mode",
      type: "string",
      description: "How edits are persisted: none | dashboard-save.",
      list: ["none", "dashboard-save"],
      default: "none"
    },
    "save-id": {
      title: "Save ID",
      type: "string",
      description: "Optional record id sent with save requests."
    },
    "save-debounce-ms": {
      title: "Save Debounce",
      type: "number",
      description: "Debounce delay before autosaving changes.",
      default: 420
    }
  },
  styling: {
    styles: [
      {
        selector: ".ua-collection-order",
        styles: [
          "display: flex;",
          "flex-direction: column;",
          "gap: 1rem;",
          "position: relative;"
        ]
      },
      {
        selector: ".ua-collection-order.is-editing .ua-collection-order-helper,.ua-collection-order.is-editing .ua-dashboard-list-toolbar,.ua-collection-order.is-editing .ua-dashboard-active-filters",
        styles: [
          "opacity: .5;",
          "transition: opacity 140ms ease;"
        ]
      },
      {
        selector: ".ua-collection-order.is-editing .ua-collection-order-edit-toggle,.ua-collection-order.is-editing [data-collection-locked-toggle='true']",
        styles: [
          "opacity: 1;"
        ]
      },
      {
        selector: ".ua-collection-order-toolbar",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .75rem;",
          "flex-wrap: wrap;"
        ]
      },
      {
        selector: ".ua-collection-order-toolbar .ua-collection-order-search",
        styles: [
          "flex: 1 1 14rem;",
          "min-width: 12rem;"
        ]
      },
      {
        selector: ".ua-collection-order-toolbar .ua-collection-toolbar-spacer",
        styles: [
          "flex: 1 1 auto;"
        ]
      },
      {
        selector: ".ua-collection-order-helper",
        styles: [
          "margin: 0;",
          "color: var(--bs-secondary-color);",
          "font-size: .95rem;"
        ]
      },
      {
        selector: ".ua-collection-order-grid",
        styles: [
          "display: grid;",
          "grid-template-columns: repeat(auto-fit, minmax(min(100%, 18rem), 1fr));",
          "gap: 1rem;",
          "align-items: stretch;",
          "grid-auto-flow: row dense;",
          "align-content: stretch;",
          "justify-content: stretch;",
          "width: 100%;",
          "min-width: 0;"
        ]
      },
      {
        selector: ".ua-collection-order-card-wrap",
        styles: [
          "grid-column: span 1;",
          "min-width: 0;",
          "transition: transform 140ms ease;"
        ]
      },
      {
        selector: ".ua-collection-order-card-wrap[data-card-size='small']",
        styles: [
          "grid-column: span 1;"
        ]
      },
      {
        selector: ".ua-collection-order-card-wrap[data-card-size='large']",
        styles: [
          "grid-column: span 2;"
        ]
      },
      {
        selector: ".ua-collection-order-card-wrap[data-card-size='full']",
        styles: [
          "grid-column: 1 / -1;"
        ]
      },
      {
        selector: ".ua-collection-order-card",
        styles: [
          "position: relative;",
          "display: flex;",
          "flex-direction: column;",
          "gap: .9rem;",
          "height: 100%;",
          "padding: 1rem;",
          "border-radius: 1.1rem;",
          "border: .0625rem solid rgba($text-color, .10);",
          "background: rgba($background-color, .92);",
          "color: $text-color;",
          "box-shadow: 0 .55rem 1.5rem rgba($text-color, .08);",
          "transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;",
          "overflow: hidden;"
        ]
      },
      {
        selector: ".ua-collection-order-card:hover",
        styles: [
          "transform: translateY(-.08rem);",
          "box-shadow: 0 .85rem 2rem rgba($text-color, .12);",
          "border-color: rgba($primary, .22);"
        ]
      },
      {
        selector: ".ua-collection-order-card[data-card-locked='true']",
        styles: [
          "border-color: rgba($secondary, .26);"
        ]
      },
      {
        selector: ".ua-collection-order.is-editing .ua-collection-order-card[data-card-locked='true']",
        styles: [
          "opacity: .48;",
          "filter: saturate(.72);",
          "box-shadow: 0 .35rem 1rem rgba($text-color, .04);",
          "transform: none;",
          "cursor: default;"
        ]
      },
      {
        selector: ".ua-collection-order.is-editing .ua-collection-order-card[data-card-locked='true']:hover",
        styles: [
          "transform: none;",
          "box-shadow: 0 .35rem 1rem rgba($text-color, .04);",
          "border-color: rgba($secondary, .26);"
        ]
      },
      {
        selector: ".ua-collection-order-card[data-card-editing='true'][data-card-locked='false']",
        styles: [
          "cursor: grab;",
          "touch-action: none;",
          "user-select: none;",
          "-webkit-user-select: none;"
        ]
      },
      {
        selector: ".ua-collection-order-card[data-card-editing='true'][data-card-locked='false']:focus-visible",
        styles: [
          "outline: .18rem solid rgba($primary, .36);",
          "outline-offset: .12rem;",
          "border-color: rgba($primary, .3);"
        ]
      },
      {
        selector: ".ua-collection-order-card.is-dragging",
        styles: [
          "opacity: .92;",
          "transform: scale(.985);",
          "box-shadow: 0 1rem 2.2rem rgba($text-color, .16);",
          "cursor: grabbing;",
          "z-index: 4;"
        ]
      },
      {
        selector: ".ua-collection-order-drag-ghost",
        styles: [
          "pointer-events: none;"
        ]
      },
      {
        selector: ".ua-collection-order-card-wrap[data-card-placeholder='true']",
        styles: [
          "position: relative;"
        ]
      },
      {
        selector: ".ua-collection-order-drop-slot",
        styles: [
          "height: 100%;",
          "border-radius: 1.1rem;",
          "border: .14rem dashed rgba($primary, .40);",
          "background: linear-gradient(180deg, rgba($primary, .08), rgba($primary, .14));",
          "box-shadow: inset 0 0 0 .08rem rgba($background-color, .75);"
        ]
      },
      {
        selector: ".ua-collection-order-card-top",
        styles: [
          "display: flex;",
          "align-items: start;",
          "gap: .75rem;"
        ]
      },
      {
        selector: ".ua-collection-order-icon",
        styles: [
          "position: relative;",
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "width: 2.75rem;",
          "height: 2.75rem;",
          "border-radius: .95rem;",
          "background: rgba($primary, .12);",
          "color: $primary;",
          "font-size: 1.25rem;",
          "flex: 0 0 auto;",
          "overflow: visible;"
        ]
      },
      {
        selector: ".ua-collection-order-icon-adornment",
        styles: [
          "position: absolute;",
          "right: -.2rem;",
          "bottom: -.2rem;",
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "width: 1.1rem;",
          "height: 1.1rem;",
          "border-radius: 999rem;",
          "background: rgba($primary, .95);",
          "color: $background-color;",
          "font-size: .62rem;",
          "box-shadow: 0 .15rem .45rem rgba($text-color, .22);",
          "z-index: 2;"
        ]
      },
      {
        selector: ".ua-collection-order-card-copy",
        styles: [
          "display: flex;",
          "flex-direction: column;",
          "gap: .35rem;",
          "min-width: 0;",
          "flex: 1 1 auto;"
        ]
      },
      {
        selector: ".ua-collection-order-overline",
        styles: [
          "font-size: .72rem;",
          "font-weight: 700;",
          "letter-spacing: .08em;",
          "text-transform: uppercase;",
          "color: var(--bs-secondary-color);"
        ]
      },
      {
        selector: ".ua-collection-order-title-row",
        styles: [
          "display: flex;",
          "align-items: start;",
          "gap: .5rem;",
          "justify-content: space-between;"
        ]
      },
      {
        selector: ".ua-collection-order-title",
        styles: [
          "font-size: 1rem;",
          "font-weight: 700;",
          "line-height: 1.25;",
          "margin: 0;",
          "overflow-wrap: break-word;",
          "word-break: normal;",
          "hyphens: auto;"
        ]
      },
      {
        selector: ".ua-collection-order-description",
        styles: [
          "margin: 0;",
          "font-size: .92rem;",
          "color: var(--bs-secondary-color);",
          "overflow-wrap: break-word;",
          "word-break: normal;"
        ]
      },
      {
        selector: ".ua-collection-order-meta",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-top: auto;",
          "font-size: .82rem;",
          "color: var(--bs-secondary-color);"
        ]
      },
      {
        selector: ".ua-collection-order-preview",
        styles: [
          "position: relative;",
          "display: flex;",
          "flex-direction: column;",
          "gap: .15rem;",
          "padding: .85rem 1rem;",
          "border-radius: .95rem;",
          "background: rgba($secondary, .10);",
          "border: .0625rem solid rgba($secondary, .12);"
        ]
      },
      {
        selector: ".ua-collection-order-preview-value",
        styles: [
          "font-size: 1.65rem;",
          "font-weight: 700;",
          "line-height: 1.05;",
          "overflow-wrap: break-word;",
          "word-break: normal;"
        ]
      },
      {
        selector: ".ua-collection-order-preview-value.is-app",
        styles: [
          "font-size: 1.2rem;",
          "line-height: 1.2;"
        ]
      },
      {
        selector: ".ua-collection-order-preview-label",
        styles: [
          "font-size: .82rem;",
          "letter-spacing: .04em;",
          "text-transform: uppercase;",
          "color: var(--bs-secondary-color);"
        ]
      },
      {
        selector: ".ua-collection-order-badge",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "padding: .18rem .55rem;",
          "border-radius: 999rem;",
          "background: rgba($secondary, .14);",
          "color: $text-color;",
          "font-size: .74rem;",
          "font-weight: 600;"
        ]
      },
      {
        selector: ".ua-collection-order-actions",
        styles: [
          "display: flex;",
          "align-items: center;",
          "justify-content: space-between;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-top: auto;"
        ]
      },
      {
        selector: ".ua-collection-order-primary-actions,.ua-collection-order-secondary-actions",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "flex-wrap: wrap;"
        ]
      },
      {
        selector: ".ua-collection-order-edit-actions",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "flex-wrap: wrap;",
          "margin-left: auto;"
        ]
      },
      {
        selector: ".ua-collection-order-lock",
        styles: [
          "position: absolute;",
          "top: .7rem;",
          "right: .7rem;",
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "width: 1.7rem;",
          "height: 1.7rem;",
          "border-radius: 999rem;",
          "background: rgba($secondary, .18);",
          "color: $secondary;",
          "font-size: .82rem;",
          "z-index: 2;"
        ]
      },
      {
        selector: ".ua-collection-order-card[data-card-has-top-adornment='true'] .ua-collection-order-lock",
        styles: [
          "right: 2.8rem;"
        ]
      },
      {
        selector: ".ua-collection-order-adornment-actions",
        styles: [
          "position: absolute;",
          "top: .7rem;",
          "right: .7rem;",
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "gap: .35rem;",
          "z-index: 3;"
        ]
      },
      {
        selector: ".ua-collection-order-adornment-actions[data-adornment-position='bottom']",
        styles: [
          "top: auto;",
          "right: .7rem;",
          "bottom: .7rem;"
        ]
      },
      {
        selector: ".ua-collection-order-adornment-actions .btn",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "width: 1.7rem;",
          "height: 1.7rem;",
          "padding: 0;",
          "border-radius: 999rem;",
          "border: .0625rem solid rgba($text-color, .08);",
          "background: rgba($background-color, .9);",
          "box-shadow: 0 .2rem .55rem rgba($text-color, .12);",
          "text-decoration: none;"
        ]
      },
      {
        selector: ".ua-collection-order-adornment-actions .ua-collection-order-open-link",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "min-height: 1.7rem;",
          "padding: 0 .65rem;",
          "border-radius: 999rem;",
          "border: .0625rem solid rgba($text-color, .08);",
          "background: rgba($background-color, .9);",
          "box-shadow: 0 .2rem .55rem rgba($text-color, .12);",
          "text-decoration: none;",
          "white-space: nowrap;"
        ]
      },
      {
        selector: ".ua-collection-order-open-link",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "gap: .35rem;",
          "color: var(--bs-secondary-color);",
          "text-decoration: none;"
        ]
      },
      {
        selector: ".ua-collection-order-edit-toggle",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "width: 2rem;",
          "height: 2rem;",
          "padding: 0;"
        ]
      },
      {
        selector: ".ua-collection-order-open-link:hover",
        styles: [
          "color: $primary;"
        ]
      },
      {
        selector: ".ua-collection-order-card[data-card-mode='preview']",
        styles: [
          "gap: .75rem;"
        ]
      },
      {
        selector: ".ua-collection-order-card[data-card-mode='preview'][data-card-has-bottom-adornment='true'] .ua-collection-order-preview",
        styles: [
          "padding-right: 3.2rem;",
          "padding-bottom: 3rem;"
        ]
      },
      {
        selector: ".ua-collection-order-preview-card-header",
        styles: [
          "display: flex;",
          "align-items: start;",
          "justify-content: space-between;",
          "gap: .75rem;"
        ]
      },
      {
        selector: ".ua-collection-order-preview-card-title",
        styles: [
          "margin: 0;",
          "font-size: .82rem;",
          "font-weight: 700;",
          "letter-spacing: .06em;",
          "text-transform: uppercase;",
          "color: var(--bs-secondary-color);"
        ]
      },
      {
        selector: ".ua-collection-order-handle",
        styles: [
          "touch-action: none;"
        ]
      },
      {
        selector: ".ua-collection-order-status",
        styles: [
          "font-size: .82rem;",
          "color: var(--bs-secondary-color);",
          "min-height: 1.2rem;"
        ]
      },
      {
        selector: ".ua-collection-order-status:not(:empty)",
        styles: [
          "display: flex;",
          "align-items: center;",
          "justify-content: space-between;",
          "gap: .75rem;",
          "padding: .65rem .8rem;",
          "border-radius: .95rem;",
          "background: rgba($background, .75);"
        ]
      },
      {
        selector: ".ua-collection-order-status-copy",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "min-width: 0;"
        ]
      },
      {
        selector: ".ua-collection-order-status-close",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "justify-content: center;",
          "width: 1.75rem;",
          "height: 1.75rem;",
          "padding: 0;",
          "border: 0;",
          "border-radius: 999rem;",
          "background: transparent;",
          "color: inherit;",
          "flex: 0 0 auto;"
        ]
      },
      {
        selector: ".ua-collection-order-status-close:hover",
        styles: [
          "background: rgba($text-color, .08);"
        ]
      },
      {
        selector: ".ua-collection-order-empty",
        styles: [
          "grid-column: 1 / -1;",
          "padding: 1.25rem;",
          "border-radius: 1rem;",
          "border: .0625rem dashed rgba($text-color, .12);",
          "background: rgba($background-color, .78);",
          "color: $text-color;"
        ]
      },
      {
        selector: ".ua-collection-order-sentinel",
        styles: [
          "grid-column: 1 / -1;",
          "height: .0625rem;"
        ]
      },
      {
        device: "ipad",
        selector: ".ua-collection-order-grid",
        styles: [
          "grid-template-columns: minmax(0, 1fr);"
        ]
      },
      {
        device: "ipad",
        selector: ".ua-collection-order-card-wrap",
        styles: [
          "grid-column: 1 / -1;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-grid",
        styles: [
          "grid-template-columns: minmax(0, 1fr);"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-card-wrap",
        styles: [
          "grid-column: 1 / -1;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-card",
        styles: [
          "padding: .9rem;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-preview-card-header",
        styles: [
          "flex-wrap: wrap;",
          "gap: .5rem;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-card[data-card-mode='preview'][data-card-has-bottom-adornment='true'] .ua-collection-order-preview",
        styles: [
          "padding-right: 1rem;",
          "padding-bottom: 3.1rem;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-toolbar",
        styles: [
          "align-items: stretch;"
        ]
      },
      {
        device: "iphone",
        selector: ".ua-collection-order-toolbar .ua-collection-order-search",
        styles: [
          "min-width: 0;",
          "width: 100%;"
        ]
      }
    ]
  }
});

var uai = require("../interface/interface.js");
var events = require("../../../uam/events.js");
var collectionClient = require("./helpers/ua.collection.order.client.js");

function parseItems(options) {
  if (options.items instanceof Array && options.items.length > 0) return options.items;
  var raw = (options["items-json"] || "").toString().trim();
  if (!raw) return [];
  try {
    var parsed = JSON.parse(raw);
    return parsed instanceof Array ? parsed : [];
  } catch (error) {
    console.warn("ua.collection.order: unable to parse items-json.", { error: error });
    return [];
  }
}

function normalizeIconClass(icon) {
  var text = (icon || "").toString().trim();
  if (!text) return "";
  if (text.indexOf("bi ") === 0) return text;
  if (text.indexOf("bi-") === 0) return "bi " + text;
  return text;
}

function normalizeItem(item, index) {
  var row = (item && typeof item === "object") ? item : {};
  var key = (row.key || row.id || row.namespace || ("item-" + index)).toString().trim();
  if (!key) key = "item-" + index;
  return {
    key: key,
    id: (row.id || key).toString(),
    title: (row.title || row.label || key).toString(),
    description: (row.description || row.subtitle || "").toString(),
    icon: normalizeIconClass(row.icon || "bi-grid"),
    route: (row.route || row.href || "").toString(),
    badge: (row.badge || "").toString(),
    meta: (row.meta || "").toString(),
    overline: (row.overline || "").toString(),
    size: (row.size || row.cardSize || "").toString().trim().toLowerCase() || "regular",
    locked: row.locked === true,
    removable: row.removable !== false,
    lockAdornment: row.lockAdornment === true,
    actionButtons: row.actionButtons instanceof Array ? row.actionButtons : [],
    cardMode: (row.cardMode || "").toString().trim().toLowerCase(),
    preview: row.preview && typeof row.preview === "object" ? { ...row.preview } : null,
    iconAdornment: row.iconAdornment && typeof row.iconAdornment === "object" ? { ...row.iconAdornment } : null,
    saveData: row.saveData && typeof row.saveData === "object" ? row.saveData : row,
    thumbnail: (row.thumbnail || row.image || row.thumb || "").toString(),
    kind: (row.kind || row.type || "item").toString(),
    order: Number(row.order) || (index + 1)
  };
}

exports.render = async function(options) {
  var uid = uai.generateRandomID(8);
  var items = parseItems(options).map(normalizeItem);
  var title = (options.title || "").toString().trim();
  var description = (options.description || "").toString().trim();
  var searchPlaceholder = (options["search-placeholder"] || "Search...").toString();
  var emptyMessage = (options["empty-message"] || "No items found.").toString();
  var apiUrl = (options["api-url"] || "").toString().trim();
  var apiFilters = options["api-filters"] && typeof options["api-filters"] === "object"
    ? JSON.stringify(options["api-filters"])
    : "{}";
  var filters = options.filters instanceof Array ? options.filters : [];
  var showToolbar = options["show-toolbar"] !== false;
  var pageNamespace = (options["page-namespace"] || "").toString().trim();
  var itemsJson = "[]";
  try {
    itemsJson = JSON.stringify(items);
  } catch (error) {}

  var controls = [];
  if (options["show-locked-toggle"] === true) {
    controls.push({
      n: "ua.toggle",
      name: (options.name || ("collectionToggle" + uid)).toString() + "Locked",
      title: (options["locked-toggle-label"] || "Locked Items").toString(),
      checked: options["show-locked"] !== false,
      "data-collection-locked-toggle": "true"
    });
  }

  if (options.editable === true) {
    controls.push({
      n: "button",
      type: "button",
      c: "btn btn-outline-secondary btn-sm ua-collection-order-edit-toggle",
      "data-collection-edit-toggle": "true",
      title: (options["edit-label"] || "Arrange").toString(),
      "aria-label": (options["edit-label"] || "Arrange").toString(),
      i: { n: "i", c: "bi bi-pencil-square" }
    });
  }

  return {
    n: "div",
    c: "ua-collection-order",
    "data-ua-collection-order": "true",
    "data-collection-name": (options.name || "").toString(),
    "data-collection-title": title || undefined,
    "data-collection-page-namespace": pageNamespace || undefined,
    "data-collection-api-url": apiUrl || undefined,
    "data-collection-api-filters": apiFilters,
    "data-collection-editable": options.editable === true ? "true" : "false",
    "data-collection-searchable": options.searchable === true ? "true" : "false",
    "data-collection-search-placeholder": searchPlaceholder,
    "data-collection-empty-message": emptyMessage,
    "data-collection-show-locked-toggle": options["show-locked-toggle"] === true ? "true" : "false",
    "data-collection-show-locked": options["show-locked"] === false ? "false" : "true",
    "data-collection-show-remove-button": options["show-remove-button"] === false ? "false" : "true",
    "data-collection-pin-visibility": (((options["pin-visibility"] || "always").toString().trim().toLowerCase()) === "edit" ? "edit" : "always"),
    "data-collection-locked-toggle-label": (options["locked-toggle-label"] || "Locked Items").toString(),
    "data-collection-edit-label": (options["edit-label"] || "Edit").toString(),
    "data-collection-page-size-sequence": (options["page-size-sequence"] || "25,25,30").toString(),
    "data-collection-api-page-size": (Number(options["api-page-size"]) || 30).toString(),
    "data-collection-save-mode": (options["save-mode"] || "none").toString(),
    "data-collection-save-id": (options["save-id"] || "").toString(),
    "data-collection-save-debounce-ms": (Number(options["save-debounce-ms"]) || 420).toString(),
    i: [
      description ? {
        n: "p",
        c: "ua-collection-order-helper",
        i: description
      } : "",
      (showToolbar && (options.searchable === true || filters.length > 0 || controls.length > 0)) ? {
        n: "ua.filter",
        name: "collectionOrderToolbar" + uid,
        searchable: options.searchable === true,
        "search-placeholder": searchPlaceholder,
        filters: filters,
        controls: controls,
        "show-active-filters": true
      } : "",
      {
        n: "div",
        c: "ua-collection-order-status",
        "data-collection-status": "true",
        i: ""
      },
      {
        n: "div",
        c: "ua-collection-order-grid",
        "data-collection-grid": "true",
        i: [
          {
            n: "div",
            c: "ua-collection-order-empty d-none",
            "data-collection-empty": "true",
            i: emptyMessage
          },
          {
            n: "div",
            c: "ua-collection-order-sentinel",
            "data-collection-sentinel": "true"
          }
        ]
      },
      {
        n: "script",
        type: "application/json",
        "data-collection-items-json": "true",
        i: itemsJson
      }
    ]
  };
};

function serializeItems(control) {
  if (!(control && control.__uaCollectionOrderState)) return [];
  var items = control.__uaCollectionOrderState.items instanceof Array ? control.__uaCollectionOrderState.items : [];
  return items.map(function(item, index) {
    var saveData = item && item.saveData && typeof item.saveData === "object" ? { ...item.saveData } : {};
    saveData.key = (item.key || saveData.key || ("item-" + index)).toString();
    saveData.order = index + 1;
    saveData.locked = item.locked === true;
    return saveData;
  });
}

async function save(name, control, repo) {
  repo.success = true;
  repo.data = serializeItems(control);
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  if (!(control)) return;
  control.__uaCollectionOrderLoadData = data;
  control.dispatchEvent(new CustomEvent("ua:collection:load", {
    detail: {
      items: data
    },
    bubbles: true
  }));
}
module.exports.load = load;

async function init(context) {
  if (!(collectionClient && typeof collectionClient.init === "function")) return;
  await collectionClient.init(context || {});
}
module.exports.init = init;

if (collectionClient && collectionClient.tests instanceof Array) {
  module.exports.tests = collectionClient.tests;
}

events.on("interface.afterrender", async function() {
  try {
    await init({});
  } catch (error) {
    console.error("ua.collection.order afterrender init failed.", error);
  }
});

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function() {
      init({}).catch(function(error) {
        console.error("ua.collection.order DOM init failed.", error);
      });
    }, { once: true });
  } else {
    setTimeout(function() {
      init({}).catch(function(error) {
        console.error("ua.collection.order DOM init failed.", error);
      });
    }, 0);
  }
}
